/**
 * Edges, clues and loops of a slitherlink. Pure functions: the TypeScript port of
 * `backend/apps/puzzletypes/slitherlink/loop.py`, kept at parity by the shared fixtures.
 *
 * The live checks at the bottom are the client's alone (CLAUDE.md §10): they read the clues
 * and the drawn edges, never the answer key.
 */
import {
  CROSSED,
  LINE,
  NO_CLUE,
  NONE,
  dotKey,
  type Cell,
  type Dot,
  type Edge,
  type EdgeState,
  type Edges,
} from "./types";

export function blankEdges(rows: number, cols: number): Edges {
  return {
    h: Array.from({ length: rows + 1 }, () => NONE.repeat(cols)),
    v: Array.from({ length: rows }, () => NONE.repeat(cols + 1)),
  };
}

export function edgesFit(edges: Edges, rows: number, cols: number): boolean {
  return (
    edges.h.length === rows + 1 &&
    edges.h.every((line) => line.length === cols) &&
    edges.v.length === rows &&
    edges.v.every((line) => line.length === cols + 1)
  );
}

export function edgeAt(edges: Edges, { axis, r, c }: Edge): EdgeState {
  const char = edges[axis][r]?.[c];
  return char === LINE || char === CROSSED ? char : NONE;
}

export function withEdge(edges: Edges, { axis, r, c }: Edge, state: EdgeState): Edges {
  const line = edges[axis][r];
  if (line === undefined || c < 0 || c >= line.length || line[c] === state) return edges;
  const lines = [...edges[axis]];
  lines[r] = line.slice(0, c) + state + line.slice(c + 1);
  return { ...edges, [axis]: lines };
}

/** The four sides of cell (r, c): top, bottom, left, right. */
export function sidesOf([r, c]: Cell): Edge[] {
  return [
    { axis: "h", r, c },
    { axis: "h", r: r + 1, c },
    { axis: "v", r, c },
    { axis: "v", r, c: c + 1 },
  ];
}

/** The edges that meet at dot (r, c), whichever exist. */
export function edgesAtDot([r, c]: Dot, rows: number, cols: number): Edge[] {
  const found: Edge[] = [];
  if (c > 0) found.push({ axis: "h", r, c: c - 1 });
  if (c < cols) found.push({ axis: "h", r, c });
  if (r > 0) found.push({ axis: "v", r: r - 1, c });
  if (r < rows) found.push({ axis: "v", r, c });
  return found;
}

/** The edge joining two orthogonally adjacent dots, or null. */
export function edgeBetween(a: Dot, b: Dot): Edge | null {
  if (a[0] === b[0] && Math.abs(a[1] - b[1]) === 1)
    return { axis: "h", r: a[0], c: Math.min(a[1], b[1]) };
  if (a[1] === b[1] && Math.abs(a[0] - b[0]) === 1)
    return { axis: "v", r: Math.min(a[0], b[0]), c: a[1] };
  return null;
}

export function endsOf({ axis, r, c }: Edge): [Dot, Dot] {
  return axis === "h"
    ? [
        [r, c],
        [r, c + 1],
      ]
    : [
        [r, c],
        [r + 1, c],
      ];
}

export function cellCount(edges: Edges, cell: Cell): number {
  return sidesOf(cell).filter((edge) => edgeAt(edges, edge) === LINE).length;
}

export function clueAt(clues: string[], r: number, c: number): number | null {
  const char = clues[r]?.[c] ?? NO_CLUE;
  return /^[0-9]$/.test(char) ? Number(char) : null;
}

/** Every clue cell whose number of surrounding segments differs from its clue, row-major. */
export function clueMismatches(clues: string[], edges: Edges, rows: number, cols: number): Cell[] {
  const found: Cell[] = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const clue = clueAt(clues, r, c);
      if (clue !== null && cellCount(edges, [r, c]) !== clue) found.push([r, c]);
    }
  return found;
}

/** Every edge drawn as a segment of the loop. */
export function segments(edges: Edges): Edge[] {
  const found: Edge[] = [];
  (["h", "v"] as const).forEach((axis) =>
    edges[axis].forEach((line, r) => {
      for (let c = 0; c < line.length; c++) if (line[c] === LINE) found.push({ axis, r, c });
    }),
  );
  return found;
}

export type LoopIssue = "empty" | "branch" | "dead_end" | "several_loops";

/** The segments grouped by connected piece, with each piece's dots and their degrees. */
function pieces(edges: Edges): { edges: Edge[]; degrees: Map<string, number> }[] {
  const links = new Map<string, Edge[]>();
  for (const edge of segments(edges))
    for (const dot of endsOf(edge)) {
      const key = dotKey(dot);
      links.set(key, [...(links.get(key) ?? []), edge]);
    }
  const seen = new Set<string>();
  const found: { edges: Edge[]; degrees: Map<string, number> }[] = [];
  for (const start of links.keys()) {
    if (seen.has(start)) continue;
    const piece = { edges: [] as Edge[], degrees: new Map<string, number>() };
    const counted = new Set<string>();
    const stack = [start];
    seen.add(start);
    while (stack.length > 0) {
      const dot = stack.pop() as string;
      const incident = links.get(dot) ?? [];
      piece.degrees.set(dot, incident.length);
      for (const edge of incident) {
        const id = `${edge.axis}:${edge.r}:${edge.c}`;
        if (!counted.has(id)) {
          counted.add(id);
          piece.edges.push(edge);
        }
        for (const other of endsOf(edge)) {
          const key = dotKey(other);
          if (!seen.has(key)) {
            seen.add(key);
            stack.push(key);
          }
        }
      }
    }
    found.push(piece);
  }
  return found;
}

/** What keeps the segments from being one closed loop; empty when they are (as in Python). */
export function loopIssues(edges: Edges): LoopIssue[] {
  const found = pieces(edges);
  if (found.length === 0) return ["empty"];
  const degrees = found.flatMap((piece) => [...piece.degrees.values()]);
  const issues: LoopIssue[] = [];
  if (degrees.some((d) => d > 2)) issues.push("branch");
  if (degrees.some((d) => d === 1)) issues.push("dead_end");
  if (issues.length > 0) return issues;
  return found.length === 1 ? [] : ["several_loops"];
}

/**
 * Every cell numbered with its count of segments, as an author does before removing some.
 * A cell closed on all four sides is left without a clue: a 4 is not a slitherlink clue.
 */
export function cluesOf(edges: Edges, rows: number, cols: number): string[] {
  return Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => {
      const n = cellCount(edges, [r, c]);
      return n < 4 ? String(n) : NO_CLUE;
    }).join(""),
  );
}

/** Whether the edges are one closed loop satisfying every clue: the verdict, client side. */
export function isSolved(clues: string[], edges: Edges, rows: number, cols: number): boolean {
  return loopIssues(edges).length === 0 && clueMismatches(clues, edges, rows, cols).length === 0;
}

// ---- live feedback ------------------------------------------------------------------------

export interface Contradictions {
  /** Clue cells that already have too many segments, or can no longer get enough. */
  clues: Cell[];
  /** Dots where three or four segments meet, or where one segment ends with no way on. */
  dots: Dot[];
  /** Segments of a closed loop that leaves other segments outside it. */
  loop: Edge[];
}

/**
 * What can no longer lead to a solution, whatever the undecided edges become. Sound, not
 * complete: anything that might still work out is left alone (CLAUDE.md §16.4).
 */
export function contradictions(
  clues: string[],
  edges: Edges,
  rows: number,
  cols: number,
): Contradictions {
  const flaggedClues: Cell[] = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const clue = clueAt(clues, r, c);
      if (clue === null) continue;
      const states = sidesOf([r, c]).map((edge) => edgeAt(edges, edge));
      const lines = states.filter((s) => s === LINE).length;
      const open = states.filter((s) => s === NONE).length;
      if (lines > clue || lines + open < clue) flaggedClues.push([r, c]);
    }

  const dots: Dot[] = [];
  for (let r = 0; r <= rows; r++)
    for (let c = 0; c <= cols; c++) {
      const states = edgesAtDot([r, c], rows, cols).map((edge) => edgeAt(edges, edge));
      const degree = states.filter((s) => s === LINE).length;
      const open = states.filter((s) => s === NONE).length;
      if (degree > 2 || (degree === 1 && open === 0)) dots.push([r, c]);
    }

  // A piece whose every dot has two segments is closed: it is the whole loop, or it is wrong.
  const found = pieces(edges);
  const closed = found.filter((piece) => [...piece.degrees.values()].every((d) => d === 2));
  const loop = found.length > 1 ? closed.flatMap((piece) => piece.edges) : [];

  return { clues: flaggedClues, dots, loop };
}

/**
 * Clue cells with exactly their count of segments: the player is done with them. A 0 has its
 * count before any move, so it only counts as done once its four sides are crossed.
 */
export function satisfiedClues(clues: string[], edges: Edges, rows: number, cols: number): Cell[] {
  const found: Cell[] = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const clue = clueAt(clues, r, c);
      if (clue === null) continue;
      const states = sidesOf([r, c]).map((edge) => edgeAt(edges, edge));
      const lines = states.filter((s) => s === LINE).length;
      if (lines === clue && (clue > 0 || !states.includes(NONE))) found.push([r, c]);
    }
  return found;
}
